import type {
  LaddersBoard as LaddersLayout,
  LaddersMoveEvent,
  LaddersRollEvent,
  LaddersStyle,
} from "@bing/engine-ladders";
import { FINAL_CELL } from "@bing/engine-ladders";
import { memo, useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { faDigits } from "../../i18n/digits";
import { fa } from "../../i18n/fa";
import {
  BOARD_ASPECT,
  cellRect,
  FRAME,
  ladderGeom,
  PAD_H,
  PAD_RECT,
  padSlot,
  PIECE_R,
  pieceLayout,
  polyPath,
  snakeGeom,
  snakeOutline,
  SQUARE,
  type LadderGeom,
  type Pt,
  type SnakeGeom,
} from "./geometry";
import {
  ladderMaterial,
  pieceTone,
  snakeSkin,
  type BoardTones,
  type LadderMaterial,
  type SnakeSkin,
} from "./looks";
import {
  canAnimate,
  motionKeyframes,
  motionMs,
  movePlan,
  playMotions,
  prefersReducedMotion,
  staySteps,
  type MotionStep,
  type MotionTrack,
} from "./motion";
import { lookFor } from "./styles";

/* ------------------------------------------------------------------ *
 * مار پله — the BOARD (card-86; round 2, batch W1). Presentational: it
 * knows the engine's types and nothing about the table, the socket or the
 * die.
 *
 *   · a STATIC SVG — the gold frame, the cells in the LOOK's two tones
 *     (`styles.ts`: the jungle's sampled from its picture, «اورجینال»'s
 *     classic ones), the ladders (wood / rope / metal), the snakes (six
 *     skins), the numbers on top with a halo (so a snake or a ladder never
 *     hides one), the gold 100 with its chest ON EVERY LOOK, the start pad.
 *     Memoised on `board.id` + `sizePx` + the look: a game's frames never
 *     repaint it.
 *   · the PIECES — an HTML layer over it, each one `translate3d`-placed, so
 *     a move is a compositor animation. A MOVE (a new `moveKey`) runs ONE
 *     finite Web Animation on the mover — the walk, the climb or slide —
 *     and one on every piece it hit, which slides home (`motion.ts`); an
 *     OVERSHOOT throw (a new `rollKey`) shakes the thrower in place. All of
 *     it is cancelled when it ends; nothing loops, nothing ticks on rAF,
 *     nothing runs while idle or hidden.
 *   · YOUR PIECE, while `movable`, is the move control: a STATIC gold ring
 *     on it and a transparent hit circle ≥ 40 px over it, above the other
 *     pieces (cells are ~24 px on a ×1.5 zoomed phone, the piece ~13).
 *
 * ⚠️ `dir="ltr"` and physical left/top only: 100 stays top-LEFT under the
 * temple even inside the app's RTL page.
 * ------------------------------------------------------------------ */

/** A seat index — `@bing/engine-core`'s `Seat`, reached through the engine (core is not a web dependency). */
type Seat = LaddersMoveEvent["seat"];

export interface LaddersBoardProps {
  readonly board: LaddersLayout; // view.board
  readonly style: LaddersStyle; // view.style — the cells' look (unknown → jungle's)
  readonly positions: readonly number[]; // view.positions — 0 is the start pad
  readonly you: Seat | null; // a STATIC ring on your piece
  readonly turn: Seat | null; // a STATIC marker on the piece on turn — never a loop
  readonly move: LaddersMoveEvent | null; // view.lastMove
  readonly moveKey: number; // view.moveCount — walk/jump/capture only when it CHANGES
  readonly roll: LaddersRollEvent | null; // view.lastRoll — an overshoot's in-place «stay» mark
  readonly rollKey: number; // view.rollCount — the stay mark only when it CHANGES
  readonly animate: boolean; // false → end state (first paint, reconnect, reduced motion, hidden)
  readonly sizePx: number; // the square's side, from LaddersScene
  readonly movable: boolean; // view.canMove — your piece: STATIC highlight + a ≥ 40 px hit area
  readonly onPieceTap?: () => void; // a tap on YOUR movable piece → W2 sends { kind: "move" }
  readonly onMotionEnd?: (moveKey: number) => void; // the motion for moveKey finished or was skipped
}

/** Your movable piece's tap target is never smaller than this, in CSS px… */
export const PIECE_HIT_MIN_PX = 40;
/** …and otherwise this many times the drawn piece. */
export const PIECE_HIT_SCALE = 2;
/** The hit circle's diameter for a board `sizePx` wide (≥ 40 px, larger than the drawn piece). */
export function pieceHitPx(sizePx: number): number {
  return Math.max(PIECE_HIT_MIN_PX, PIECE_HIT_SCALE * 2 * PIECE_R * (sizePx / SQUARE));
}

const VIEW_H = SQUARE + PAD_H;
const n3 = (v: number): number => Math.round(v * 1000) / 1000;

/* ---- the static picture --------------------------------------------------------------------- */

function Defs({ T }: { T: BoardTones }): ReactNode {
  return (
    <defs>
      {/* The gold frame — the app's own golds (`--c-gold-deep`, `--accent`). */}
      <linearGradient id="lad-frame" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" style={{ stopColor: "rgb(var(--c-gold-deep))" }} />
        <stop offset="0.38" style={{ stopColor: "var(--accent)" }} />
        <stop offset="0.5" stopColor="#f7e3a1" />
        <stop offset="0.62" style={{ stopColor: "var(--accent)" }} />
        <stop offset="1" style={{ stopColor: "rgb(var(--c-gold-deep))" }} />
      </linearGradient>
      <radialGradient id="lad-cell-dark" cx="0.5" cy="0.42" r="0.75">
        <stop offset="0" stopColor={T.dark.glow} />
        <stop offset="1" stopColor={T.dark.base} />
      </radialGradient>
      <radialGradient id="lad-cell-light" cx="0.5" cy="0.42" r="0.75">
        <stop offset="0" stopColor={T.light.glow} />
        <stop offset="1" stopColor={T.light.base} />
      </radialGradient>
      {/* THE 100 CELL: glowing gold — gradients only, painted once (never a pulse, never a filter). */}
      <radialGradient id="lad-goal" cx="0.5" cy="0.55" r="0.7">
        <stop offset="0" stopColor="#fff6c4" />
        <stop offset="0.45" stopColor="#f3cf5a" />
        <stop offset="1" style={{ stopColor: "rgb(var(--c-gold-deep))" }} />
      </radialGradient>
      <radialGradient id="lad-goal-halo" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stopColor="#fff0a8" stopOpacity="0.95" />
        <stop offset="0.42" stopColor="#ffd25a" stopOpacity="0.5" />
        <stop offset="1" stopColor="#ffc83c" stopOpacity="0" />
      </radialGradient>
    </defs>
  );
}

function Frame({ T }: { T: BoardTones }): ReactNode {
  return (
    <g data-part="frame">
      <rect x={0} y={0} width={SQUARE} height={SQUARE} rx={0.3} fill="url(#lad-frame)" />
      <rect
        x={0.05}
        y={0.05}
        width={SQUARE - 0.1}
        height={SQUARE - 0.1}
        rx={0.26}
        fill="none"
        stroke="#fff3c8"
        strokeOpacity={0.55}
        strokeWidth={0.03}
      />
      <rect
        x={FRAME - 0.05}
        y={FRAME - 0.05}
        width={SQUARE - 2 * FRAME + 0.1}
        height={SQUARE - 2 * FRAME + 0.1}
        rx={0.1}
        fill={T.grout}
        stroke="#4d3a12"
        strokeWidth={0.04}
      />
    </g>
  );
}

function Cells(): ReactNode {
  const out: ReactNode[] = [];
  for (let n = 1; n <= FINAL_CELL; n += 1) {
    const r = cellRect(n);
    const light = (Math.round(r.x - FRAME) + Math.round(r.y - FRAME)) % 2 === 0;
    const goal = n === FINAL_CELL;
    out.push(
      <rect
        key={n}
        data-cell={n}
        x={n3(r.x + 0.025)}
        y={n3(r.y + 0.025)}
        width={0.95}
        height={0.95}
        rx={0.09}
        fill={goal ? "url(#lad-goal)" : light ? "url(#lad-cell-light)" : "url(#lad-cell-dark)"}
        stroke={goal ? "#fff1b8" : "none"}
        strokeWidth={goal ? 0.04 : 0}
      />,
    );
  }
  return <g data-part="cells">{out}</g>;
}

function Ladder({ g, m }: { g: LadderGeom; m: LadderMaterial }): ReactNode {
  const [left, right] = g.rails;
  const line = (a: Pt, b: Pt, stroke: string, w: number, extra?: object): ReactNode => (
    <line
      x1={n3(a.x)}
      y1={n3(a.y)}
      x2={n3(b.x)}
      y2={n3(b.y)}
      stroke={stroke}
      strokeWidth={w}
      strokeLinecap="round"
      {...extra}
    />
  );
  const shadow = { x: 0.05, y: 0.07 };
  const sh = (p: Pt): Pt => ({ x: p.x + shadow.x, y: p.y + shadow.y });
  return (
    <g data-ladder={g.index} data-material={m.kind}>
      {/* A drop shadow without a filter: the same strokes, offset, in translucent black. */}
      <g opacity={0.32}>
        {line(sh(left[0]), sh(left[1]), "#000", m.railW + 0.04)}
        {line(sh(right[0]), sh(right[1]), "#000", m.railW + 0.04)}
      </g>
      {g.rungs.map(([a, b], k) => (
        <g key={k}>
          {line(a, b, m.edge, m.rungW + 0.035)}
          {line(a, b, m.rung, m.rungW)}
        </g>
      ))}
      {[left, right].map(([a, b], k) => (
        <g key={`r${k}`}>
          {line(a, b, m.edge, m.railW + 0.04)}
          {line(a, b, m.rail, m.railW)}
          {m.kind === "rope"
            ? line(a, b, m.detail, m.railW * 0.7, { strokeDasharray: "0.05 0.07" })
            : line(a, b, m.railLight, m.railW * 0.32, { strokeOpacity: 0.85 })}
        </g>
      ))}
      {m.kind !== "wood"
        ? g.rungs.flatMap(([a, b], k) =>
            [a, b].map((p, j) => (
              <circle
                key={`k${k}-${j}`}
                cx={n3(p.x)}
                cy={n3(p.y)}
                r={m.kind === "rope" ? 0.055 : 0.035}
                fill={m.kind === "rope" ? m.detail : m.railLight}
                stroke={m.edge}
                strokeWidth={0.015}
              />
            )),
          )
        : null}
    </g>
  );
}

function SnakeMarks({ g, k }: { g: SnakeGeom; k: SnakeSkin }): ReactNode {
  const last = g.spine.length - 1;
  const at = (t: number): { p: Pt; n: Pt; w: number; i: number } => {
    const i = Math.max(0, Math.min(last, Math.round(t * last)));
    return { p: g.spine[i]!, n: g.normal[i]!, w: g.halfWidth[i]!, i };
  };
  const len = g.spine.reduce(
    (s, p, i) => (i === 0 ? 0 : s + Math.hypot(p.x - g.spine[i - 1]!.x, p.y - g.spine[i - 1]!.y)),
    0,
  );
  const ts = (gap: number, from = 0.16, to = 0.9): number[] => {
    const out: number[] = [];
    const dt = gap / Math.max(len, 0.5);
    for (let t = from; t <= to; t += dt) out.push(t);
    return out;
  };
  const off = (p: Pt, n: Pt, d: number): Pt => ({ x: p.x + n.x * d, y: p.y + n.y * d });
  switch (k.pattern) {
    case "diamonds":
      return (
        <g fill={k.mark} stroke={k.mark2} strokeWidth={0.014}>
          {ts(0.34).map((t, j) => {
            const a = at(t);
            const tan = { x: a.n.y, y: -a.n.x };
            const along = 0.1;
            const across = a.w * 0.72;
            return (
              <path
                key={j}
                d={polyPath(
                  [
                    off(a.p, tan, along),
                    off(a.p, a.n, across),
                    off(a.p, tan, -along),
                    off(a.p, a.n, -across),
                  ],
                  true,
                )}
              />
            );
          })}
        </g>
      );
    case "bands":
      return (
        <g>
          {ts(0.26).map((t, j) => {
            const a = at(t - 0.025);
            const b = at(t + 0.025);
            return (
              <path
                key={j}
                fill={j % 2 === 0 ? k.mark : k.mark2}
                d={polyPath(
                  [
                    off(a.p, a.n, a.w * 0.96),
                    off(b.p, b.n, b.w * 0.96),
                    off(b.p, b.n, -b.w * 0.96),
                    off(a.p, a.n, -a.w * 0.96),
                  ],
                  true,
                )}
              />
            );
          })}
        </g>
      );
    case "spots":
      return (
        <g fill={k.mark}>
          {ts(0.22).map((t, j) => {
            const a = at(t);
            const p = off(a.p, a.n, (j % 2 === 0 ? 1 : -1) * a.w * 0.38);
            return <circle key={j} cx={n3(p.x)} cy={n3(p.y)} r={n3(a.w * 0.34)} />;
          })}
        </g>
      );
    case "stripes":
      return <path d={snakeOutline(g, 0.34)} fill={k.mark} opacity={0.9} />;
    case "scales":
      return (
        <g fill="none" stroke={k.mark} strokeWidth={0.026} strokeLinecap="round">
          {ts(0.16).map((t, j) => {
            const a = at(t);
            const b = at(t + 0.05 / Math.max(len, 0.5));
            return (
              <path
                key={j}
                d={polyPath([off(b.p, b.n, b.w * 0.68), a.p, off(b.p, b.n, -b.w * 0.68)])}
              />
            );
          })}
        </g>
      );
    case "zigzag": {
      const pts = ts(0.13, 0.12, 0.92).map((t, j) => {
        const a = at(t);
        return off(a.p, a.n, (j % 2 === 0 ? 1 : -1) * a.w * 0.6);
      });
      return (
        <path
          d={polyPath(pts)}
          fill="none"
          stroke={k.mark}
          strokeWidth={0.05}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      );
    }
  }
}

function Snake({ g, k }: { g: SnakeGeom; k: SnakeSkin }): ReactNode {
  const deg = (Math.atan2(g.facing.y, g.facing.x) * 180) / Math.PI;
  const sheen = g.spine
    .map((p, i) => ({
      x: p.x + g.normal[i]!.x * g.halfWidth[i]! * 0.45,
      y: p.y + g.normal[i]!.y * g.halfWidth[i]! * 0.45,
    }))
    .slice(Math.round(g.spine.length * 0.06), Math.round(g.spine.length * 0.82));
  return (
    <g data-snake={g.index} data-skin={k.pattern}>
      <path d={snakeOutline(g)} transform="translate(0.05 0.07)" fill="#000" opacity={0.3} />
      <path
        d={snakeOutline(g)}
        fill={k.body}
        stroke={k.edge}
        strokeWidth={0.035}
        strokeLinejoin="round"
      />
      <SnakeMarks g={g} k={k} />
      <path
        d={polyPath(sheen)}
        fill="none"
        stroke={k.sheen}
        strokeOpacity={0.55}
        strokeWidth={0.03}
        strokeLinecap="round"
      />
      {/* The head ON the head cell's centre, looking away from its body. */}
      <g transform={`translate(${n3(g.head.x)} ${n3(g.head.y)}) rotate(${n3(deg)})`}>
        <path
          d="M0.4 0 L0.47 -0.045 M0.4 0 L0.47 0.045 M0.24 0 L0.4 0"
          fill="none"
          stroke="#e3343c"
          strokeWidth={0.024}
          strokeLinecap="round"
        />
        <ellipse
          cx={0.03}
          cy={0}
          rx={0.25}
          ry={0.195}
          fill={k.edge}
          opacity={0.35}
          transform="translate(0.03 0.05)"
        />
        <ellipse
          cx={0.03}
          cy={0}
          rx={0.25}
          ry={0.195}
          fill={k.body}
          stroke={k.edge}
          strokeWidth={0.035}
        />
        <ellipse cx={0.07} cy={0} rx={0.13} ry={0.07} fill={k.sheen} opacity={0.45} />
        {[-1, 1].map((sgn) => (
          <g key={sgn}>
            <circle
              cx={0.1}
              cy={sgn * 0.095}
              r={0.058}
              fill="#fffbe8"
              stroke={k.edge}
              strokeWidth={0.016}
            />
            <ellipse cx={0.118} cy={sgn * 0.095} rx={0.02} ry={0.038} fill="#151008" />
          </g>
        ))}
      </g>
    </g>
  );
}

/** A cell number's size on a roomy board, in board units (a cell is 1). */
export const NUMBER_FONT_MIN = 0.3;
/** …its largest, on a small board: above this the numbers would hide the snakes and ladders. */
export const NUMBER_FONT_MAX = 0.46;
/** The size, in CSS px, a cell number never drops under while the cell has room for it. */
export const NUMBER_MIN_PX = 9.5;

/**
 * A cell number's font size in board units for a board `sizePx` wide: 0.3 of a cell where that is
 * already ≥ {@link NUMBER_MIN_PX} — or larger on a small board (a zoomed phone, a Desktop window),
 * up to {@link NUMBER_FONT_MAX}, so the digits stay readable (card-86 review: 4–5 px at 240×533 and
 * 307×472). Pure: the tests pin it.
 */
export function numberFontUnits(sizePx: number): number {
  const cellPx = sizePx / SQUARE;
  if (!(cellPx > 0)) return NUMBER_FONT_MIN;
  return Math.min(NUMBER_FONT_MAX, Math.max(NUMBER_FONT_MIN, NUMBER_MIN_PX / cellPx));
}

function Numbers({ sizePx, T }: { sizePx: number; T: BoardTones }): ReactNode {
  const fs = numberFontUnits(sizePx);
  const out: ReactNode[] = [];
  for (let n = 1; n <= FINAL_CELL; n += 1) {
    const r = cellRect(n);
    out.push(
      // The baseline moves down with the size, so a bigger number still sits in its cell's corner.
      <text key={n} x={n3(r.x + 0.07)} y={n3(r.y + 0.06 + fs * 0.93)} data-num={n}>
        {faDigits(n)}
      </text>,
    );
  }
  return (
    // ABOVE every snake and ladder, with a dark halo: a number is never hidden by the art.
    <g
      data-part="numbers"
      fontFamily="Vazirmatn, system-ui, sans-serif"
      fontWeight={700}
      fontSize={n3(fs)}
      fill={T.ink}
      stroke={T.halo}
      strokeWidth={n3(fs * 0.283)}
      strokeLinejoin="round"
      paintOrder="stroke"
      style={{ userSelect: "none" }}
    >
      {out}
    </g>
  );
}

/** The 100 cell's treasure: a small gold-strapped chest, drawn in SVG (static). */
function Goal(): ReactNode {
  const r = cellRect(FINAL_CELL);
  const cx = r.x + 0.55;
  const cy = r.y + 0.64;
  return (
    <g data-part="goal" data-testid="ladders-goal">
      {/* The glow spills softly past the cell — a gradient, painted once. */}
      <circle cx={n3(r.x + 0.5)} cy={n3(r.y + 0.5)} r={1.2} fill="url(#lad-goal-halo)" />
      <g data-part="chest" transform={`translate(${n3(cx)} ${n3(cy)}) scale(0.5)`}>
        <ellipse cx={0} cy={0.44} rx={0.56} ry={0.09} fill="#000" opacity={0.25} />
        <rect
          x={-0.5}
          y={-0.06}
          width={1}
          height={0.48}
          rx={0.05}
          fill="#8b5527"
          stroke="#3a1f08"
          strokeWidth={0.05}
        />
        <path
          d="M-0.5 -0.06 L-0.5 -0.2 Q0 -0.52 0.5 -0.2 L0.5 -0.06 Z"
          fill="#b06e30"
          stroke="#3a1f08"
          strokeWidth={0.05}
          strokeLinejoin="round"
        />
        <rect
          x={-0.52}
          y={-0.1}
          width={1.04}
          height={0.09}
          fill="#f5cf55"
          stroke="#7a560c"
          strokeWidth={0.03}
        />
        {[-0.3, 0.3].map((x) => (
          <rect
            key={x}
            x={x - 0.06}
            y={-0.36}
            width={0.12}
            height={0.78}
            fill="#f5cf55"
            stroke="#7a560c"
            strokeWidth={0.03}
          />
        ))}
        <rect
          x={-0.09}
          y={-0.06}
          width={0.18}
          height={0.2}
          rx={0.03}
          fill="#ffe07a"
          stroke="#7a560c"
          strokeWidth={0.03}
        />
        <circle cx={0} cy={0.03} r={0.03} fill="#3a1f08" />
        <path
          d="M0.38 -0.5 L0.42 -0.42 L0.5 -0.38 L0.42 -0.34 L0.38 -0.26 L0.34 -0.34 L0.26 -0.38 L0.34 -0.42 Z"
          fill="#fffbe0"
        />
      </g>
    </g>
  );
}

function Pad({ T }: { T: BoardTones }): ReactNode {
  return (
    <g data-part="pad">
      <rect
        x={PAD_RECT.x}
        y={PAD_RECT.y}
        width={PAD_RECT.w}
        height={PAD_RECT.h}
        rx={PAD_RECT.h / 2}
        fill={T.pad}
        fillOpacity={T.padOpacity}
        style={{ stroke: "rgb(var(--c-gold-deep))" }}
        strokeWidth={0.035}
      />
      {[0, 1, 2, 3].map((seat) => {
        const p = padSlot(seat);
        return (
          <circle
            key={seat}
            cx={n3(p.x)}
            cy={n3(p.y)}
            r={PIECE_R * 0.92}
            fill="none"
            stroke={T.padSlot}
            strokeOpacity={0.3}
            strokeWidth={0.025}
          />
        );
      })}
    </g>
  );
}

/** The whole static picture — painted once per board, size and look. */
const BoardArt = memo(
  function BoardArt({
    board,
    sizePx,
    tones,
  }: {
    board: LaddersLayout;
    sizePx: number;
    tones: BoardTones;
  }): ReactNode {
    const ladders = board.ladders.map((_, i) => ladderGeom(board, i));
    const snakes = board.snakes.map((_, i) => snakeGeom(board, i));
    return (
      <svg
        data-testid="ladders-art"
        width={sizePx}
        height={sizePx * BOARD_ASPECT}
        viewBox={`0 0 ${SQUARE} ${VIEW_H}`}
        style={{ position: "absolute", left: 0, top: 0, display: "block", overflow: "visible" }}
        aria-hidden
      >
        <Defs T={tones} />
        <Frame T={tones} />
        <Cells />
        <Goal />
        <g data-part="ladders">
          {ladders.map((g) => (
            <Ladder key={g.index} g={g} m={ladderMaterial(g.index)} />
          ))}
        </g>
        <g data-part="snakes">
          {snakes.map((g) => (
            <Snake key={g.index} g={g} k={snakeSkin(g.index)} />
          ))}
        </g>
        <Numbers sizePx={sizePx} T={tones} />
        <Pad T={tones} />
      </svg>
    );
  },
  (a, b) => a.board.id === b.board.id && a.sizePx === b.sizePx && a.tones === b.tones,
);

/* ---- the board --------------------------------------------------------------------------- */

/** A running motion: its end (`notify` false — an unmount — ends it silently). */
interface Running {
  readonly end: (notify?: boolean) => void;
}

export function LaddersBoard({
  board,
  style,
  positions,
  you,
  turn,
  move,
  moveKey,
  roll,
  rollKey,
  animate,
  sizePx,
  movable,
  onPieceTap,
  onMotionEnd,
}: LaddersBoardProps): ReactNode {
  const tones = lookFor(style).tones;
  const u = sizePx / SQUARE;
  const d = 2 * PIECE_R * u;
  const layout = pieceLayout(positions);
  const pieceEls = useRef<(HTMLDivElement | null)[]>([]);
  const adoptedMove = useRef<number | null>(null);
  const adoptedRoll = useRef<number | null>(null);
  const running = useRef<Running | null>(null);
  const onEnd = useRef(onMotionEnd);
  onEnd.current = onMotionEnd;
  // The latest props for the effects below, which are keyed on the COUNTERS alone (every frame is
  // fresh JSON: depending on `move` would re-run them on every update and replay the move).
  const latest = useRef({ board, positions, move, roll, animate, sizePx });
  latest.current = { board, positions, move, roll, animate, sizePx };

  /** Start `tracks` as one motion of `ms`; `done` runs when it ends (or is ended, notified). */
  const start = (tracks: readonly MotionTrack[], ms: number, done: () => void): void => {
    let self: Running | null = null;
    let ended = false;
    // The app hidden mid-motion: end it now (a hidden page may never deliver `finish`).
    const onHide = (): void => {
      if (document.hidden) self?.end();
    };
    const detach = (): void => document.removeEventListener("visibilitychange", onHide);
    const finish = playMotions(tracks, ms, () => {
      ended = true;
      detach();
      if (running.current === self) running.current = null;
      done();
    });
    if (ended) return; // nothing could play: already reported, nothing to watch
    self = {
      end: (notify = true): void => {
        detach();
        finish(notify);
      },
    };
    document.addEventListener("visibilitychange", onHide);
    running.current = self;
  };
  const allowed = (el: HTMLElement): boolean =>
    canAnimate({
      animate: latest.current.animate,
      hidden: document.hidden,
      reducedMotion: prefersReducedMotion(),
      supported: typeof el.animate === "function",
    });
  /** Steps → keyframes in px for a piece's `translate3d` (its box is the drawn disc). */
  const framesOf = (steps: readonly MotionStep[]): Keyframe[] => {
    const px = latest.current.sizePx / SQUARE;
    const r = PIECE_R * px;
    return motionKeyframes(steps).map((k) => ({
      transform: `translate3d(${n3(k.x * px - r)}px, ${n3(k.y * px - r)}px, 0) scale(${k.scale})`,
      offset: k.offset,
      easing: k.easing,
    }));
  };

  /* A MOVE. A LAYOUT effect: React has just placed the pieces at their END; the animation must be
   * on them before the browser paints, or the end positions flash first. */
  useLayoutEffect(() => {
    const key = moveKey;
    const report = (): void => onEnd.current?.(key);
    if (adoptedMove.current === null) {
      // First paint (a mount, a resume, a reconnect): adopt the move — never replay it.
      adoptedMove.current = key;
      report();
      return;
    }
    if (key === adoptedMove.current) return;
    adoptedMove.current = key;
    // A new move ends the old motion at once (its end state is the new motion's start).
    running.current?.end();
    running.current = null;
    const now = latest.current;
    const m = now.move;
    const mover = m ? (pieceEls.current[m.seat] ?? null) : null;
    if (!m || !mover || m.seat >= now.positions.length || !allowed(mover)) {
      report();
      return;
    }
    // Before the move: the mover on its old cell, every piece it hit on the cell it was hit on.
    const before = now.positions.slice();
    before[m.seat] = m.from;
    for (const hit of m.captured) if (hit.seat < before.length) before[hit.seat] = hit.cell;
    const plan = movePlan(now.board, m, pieceLayout(before), pieceLayout(now.positions));
    const tracks = plan.parts.flatMap((part) => {
      const el = pieceEls.current[part.seat];
      return el ? [{ el, frames: framesOf(part.steps) }] : [];
    });
    if (tracks.length === 0 || plan.ms <= 0) {
      report();
      return;
    }
    start(tracks, plan.ms, report);
  }, [moveKey]);

  /* A THROW: only an OVERSHOOT moves anything on the board — the thrower's piece shakes in place
   * once the die has landed and been read («you need exactly N»). Any other throw is the die's. */
  useLayoutEffect(() => {
    const key = rollKey;
    if (adoptedRoll.current === null) {
      adoptedRoll.current = key;
      return;
    }
    if (key === adoptedRoll.current) return;
    adoptedRoll.current = key;
    const now = latest.current;
    const r = now.roll;
    if (!r || !r.overshoot || r.seat >= now.positions.length) return;
    // A new mark ends the old motion at once (a move still walking is told it ended).
    running.current?.end();
    running.current = null;
    const el = pieceEls.current[r.seat] ?? null;
    const at = pieceLayout(now.positions)[r.seat];
    if (!el || !at || !allowed(el)) return;
    const steps = staySteps(at);
    start([{ el, frames: framesOf(steps) }], motionMs(steps), () => undefined);
  }, [rollKey]);

  // A resize mid-motion: the keyframes were in the old size's pixels — end at the new layout.
  useEffect(() => {
    running.current?.end();
  }, [sizePx]);
  // Unmount: cancel silently.
  useEffect(() => () => running.current?.end(false), []);

  const hit = movable && you !== null && you < layout.length ? (layout[you] ?? null) : null;
  const hitPx = pieceHitPx(sizePx);
  const hitSeats = new Set((move?.captured ?? []).map((c) => c.seat));

  return (
    <div
      dir="ltr"
      data-testid="ladders-board"
      data-style={lookFor(style).id}
      className="ladders-board"
      style={{ position: "relative", width: sizePx, height: sizePx * BOARD_ASPECT }}
    >
      <BoardArt board={board} sizePx={sizePx} tones={tones} />
      <div
        className="ladders-pieces"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
        }}
      >
        {layout.map((p, seat) => {
          const tone = pieceTone(seat);
          const mine = you === seat;
          const onTurn = turn === seat;
          const canGo = mine && hit !== null;
          // Every ring is a STATIC box-shadow — never a pulse. Your movable piece carries the
          // strongest one: white, then a wide gold band and a soft gold glow.
          const rings = [
            mine ? "0 0 0 2px #fffdf0" : null,
            canGo
              ? "0 0 0 5px var(--accent)"
              : onTurn
                ? `0 0 0 ${mine ? 4 : 2}px var(--accent)`
                : null,
            canGo
              ? "0 0 12px 5px rgba(255,214,90,0.85)"
              : onTurn
                ? "0 0 9px 2px rgba(233,195,73,0.7)"
                : null,
            "0 2px 4px rgba(0,0,0,0.55)",
          ].filter(Boolean);
          return (
            <div
              key={seat}
              ref={(el) => {
                pieceEls.current[seat] = el;
              }}
              data-testid={`ladders-piece-${seat}`}
              data-cell={positions[seat]}
              data-you={mine ? "1" : undefined}
              data-turn={onTurn ? "1" : undefined}
              data-movable={canGo ? "1" : undefined}
              className="ladders-piece"
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: d,
                height: d,
                borderRadius: "50%",
                transform: `translate3d(${n3(p.x * u - d / 2)}px, ${n3(p.y * u - d / 2)}px, 0)`,
                background: `radial-gradient(circle at 34% 30%, ${tone.light} 0%, ${tone.base} 62%, rgba(0,0,0,0.5) 100%)`,
                border: "1px solid rgba(0,0,0,0.55)",
                boxShadow: rings.join(", "),
                boxSizing: "border-box",
                zIndex:
                  (move?.seat === seat ? 30 : hitSeats.has(seat) ? 20 : 0) +
                  (canGo ? 40 : onTurn ? 10 : 0) +
                  seat,
              }}
            />
          );
        })}
        {hit ? (
          // YOUR PIECE'S TAP TARGET: a transparent circle ≥ 40 px centred on it, above every piece.
          <button
            type="button"
            data-testid="ladders-piece-hit"
            aria-label={fa.ladders.movePiece}
            className="ladders-piece-hit"
            onClick={() => onPieceTap?.()}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: hitPx,
              height: hitPx,
              transform: `translate3d(${n3(hit.x * u - hitPx / 2)}px, ${n3(hit.y * u - hitPx / 2)}px, 0)`,
              zIndex: 100,
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
